// noinspection GraphQLUnresolvedReference

import type { GraphqlClientOptionsProps } from '../../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../../base/api/graphqlClient.ts'
import { invalidateQuery, type InvalidateQueryOptions, useApiQuery } from '../../../../base/api/queryUtils.ts'
import type { QueryOpts } from '../../../../base/types/reactQuery.ts'
import { gqlAst } from '../../../../utils/gql.ts'
import { impresoraPuntoVentaFragmentAst } from '../fragments/impresoraFragment.ts'
import { ImpresoraPuntoVentaProps } from '../interfaces/impresora.ts'

const query = /* GraphQL */ gqlAst`
  ${impresoraPuntoVentaFragmentAst}

  query IMPRESORAS_PUNTO_VENTA($codigoSucursal: Int!, $codigoPuntoVenta: Int!) {
    impresorasPuntoVenta(codigoSucursal: $codigoSucursal, codigoPuntoVenta: $codigoPuntoVenta) {
      licencia
      mensaje
      asignaciones {
        ...impresoraPuntoVentaFields
      }
    }
  }
`

export const KEY_IMPRESORA_PUNTO_VENTA = 'impresora-punto-venta'

export interface ApiImpresoraPuntoVentaParams {
  codigoSucursal: number
  codigoPuntoVenta: number
}

/** Impresoras listas para imprimir en un punto de venta. */
export interface ApiImpresoraPuntoVentaResponse {
  /** Si hay licencia de producto IMPRESION vigente; sin ella las asignaciones no son funcionales */
  licencia: boolean
  /** Mensaje informativo del backend (ej. motivo de licencia no vigente) */
  mensaje: string | null
  /** Asignaciones resueltas: propias del punto de venta o, en su defecto, de la sucursal */
  asignaciones: ImpresoraPuntoVentaProps[]
}

/**
 * Impresoras asignadas a un punto de venta, ya resueltas para imprimir con isi-print.
 * @param params Código de sucursal y de punto de venta de la sesión.
 * @param options Headers adicionales y señal de cancelación de la petición.
 * @author isi-template
 */
export const apiImpresoraPuntoVenta = async (
  params: ApiImpresoraPuntoVentaParams,
  options: GraphqlClientOptionsProps = {},
): Promise<ApiImpresoraPuntoVentaResponse> => {
  try {
    const data = await graphqlClient.request<{ impresorasPuntoVenta: ApiImpresoraPuntoVentaResponse }>({
      document: query,
      variables: { ...params },
      requestHeaders: options.headers,
      signal: options.signal,
    })
    return data.impresorasPuntoVenta
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Invalida las impresoras del punto de venta desde cualquier lugar (ej. onSuccess de mutaciones),
 * sin necesidad de tener montado el hook.
 * @example invalidateImpresoraPuntoVenta()
 * @example invalidateImpresoraPuntoVenta({ extraQueryKeys: [codigoSucursal, codigoPuntoVenta] })
 * @author isi-template
 */
export const invalidateImpresoraPuntoVenta = (options?: InvalidateQueryOptions) =>
  invalidateQuery(KEY_IMPRESORA_PUNTO_VENTA, options)

/**
 * Hook para consultar las impresoras de un punto de venta.
 * queryKey: [KEY_IMPRESORA_PUNTO_VENTA, codigoSucursal, codigoPuntoVenta, ...(options.queryKeys ?? [])]
 * Para invalidar sin montar el hook, usar `invalidateImpresoraPuntoVenta()`.
 * @param params Código de sucursal y de punto de venta de la sesión.
 * @param options Opciones de React Query con soporte de queryKeys adicionales.
 * @author isi-template
 */
export const useImpresoraPuntoVenta = (
  params: ApiImpresoraPuntoVentaParams,
  options: QueryOpts<ApiImpresoraPuntoVentaResponse> = {},
) =>
  useApiQuery(KEY_IMPRESORA_PUNTO_VENTA, (ctx) => apiImpresoraPuntoVenta(params, { signal: ctx.signal }), {
    refetchOnWindowFocus: false,
    ...options,
    queryKeys: [params.codigoSucursal, params.codigoPuntoVenta, ...(options.queryKeys ?? [])],
  })
